import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const HEADER_OFFSET = 96;

const scrollToId = (id: string, attempt = 0) => {
  const el = document.getElementById(id);
  if (!el) {
    // Section may not have rendered yet after a route change — retry briefly.
    if (attempt < 20) requestAnimationFrame(() => scrollToId(id, attempt + 1));
    return;
  }
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top: top < 0 ? 0 : top, behavior: "smooth" });
};

/**
 * Some triggers (e.g. dropdown menus) restore focus after closing, which can
 * cancel the scroll. Re-assert the target position shortly after.
 */
const scrollToIdReliably = (id: string) => {
  scrollToId(id);
  window.setTimeout(() => scrollToId(id), 150);
  window.setTimeout(() => scrollToId(id), 400);
};

/**
 * Makes homepage anchor links (href="/#section") work from any route:
 * intercepts the click, routes to "/" when needed, then smooth-scrolls
 * to the section once it has rendered.
 */
const HashScrollHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll when the hash is present on the current location (incl. after navigation).
  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    requestAnimationFrame(() => scrollToIdReliably(id));
  }, [location.pathname, location.hash, location.key]);

  // Intercept clicks on homepage anchor links so the router handles them.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      if (anchor.target && anchor.target !== "_self") return;
      const raw = anchor.getAttribute("href");
      if (!raw || !raw.startsWith("/#")) return;
      const id = decodeURIComponent(raw.slice(2));
      if (!id) return;
      e.preventDefault();
      if (window.location.pathname === "/") {
        window.history.replaceState(window.history.state, "", `/#${id}`);
        scrollToIdReliably(id);
      } else {
        navigate(`/#${id}`);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);

  return null;
};

export default HashScrollHandler;